'use client';

import Image from '@/components/Image';
import MagicJump, { type JumpTarget } from '@/components/MagicJump';
import { useValidExposure } from '@/hooks/useValidExposure';
import logger from '@/utils/client-logger';
import { useSpmDomAttr, withSpmCnt } from '@/reporting/spm';
import { useHomeSpmModuleCode } from '@/app/(home-page)/utils/spm-module';

import type {
  OneRowFourNavItem,
  OneRowFourNavProps,
  OneRowFourNavValidExposureParams,
} from './types';

const MAX_NAV_ITEMS = 5;

/**
 * 服务保障坑位（service_entry_NN）按标题语义匹配：
 * 截图证据显示可见顺序为 365 Days / 30-Day Returns / Trustpilot / FSA/HSA，
 * 编码只覆盖 365、30-Day、FSA/HSA 三项（Trustpilot 无编码），不能按纯索引映射。
 */
const SERVICE_PROMISE_SPM_RULES: Array<{ pattern: RegExp; position: string }> = [
  { pattern: /365\s*days?/i, position: 'service_entry_01' },
  { pattern: /30[\s-]*day/i, position: 'service_entry_02' },
  { pattern: /fsa\s*\/?\s*hsa/i, position: 'service_entry_03' },
];

const getServicePromiseSpmPosition = (title: string): string | undefined => {
  const plainTitle = title.replace(/<[^>]*>/g, ' ');
  return SERVICE_PROMISE_SPM_RULES.find((rule) => rule.pattern.test(plainTitle))?.position;
};

function OneRowFourNavItemView({
  item,
  index,
  componentId,
  moduleId,
  moduleIndex,
  moduleTitle,
  spmModule,
  onTrack,
  onValidExpTrack,
}: {
  item: OneRowFourNavItem & { imageSrc: string };
  index: number;
  componentId: string;
  moduleId: string;
  moduleIndex: number;
  moduleTitle: string;
  /** 楼层完整动态 SPM 模块编码（C 位，`${component_id}_${id}`）。 */
  spmModule: string;
} & Pick<OneRowFourNavProps, 'onTrack' | 'onValidExpTrack'>) {
  const trackingParams: OneRowFourNavValidExposureParams = {
    module_index: moduleIndex,
    module_id: moduleId,
    module_title: moduleTitle,
    module_name: componentId,
    module_tab_name: '',
    mkt_index: index,
    mkt_index_sub: '',
    mkt_jump_type: String(item.jump_type ?? ''),
    mkt_jump_content: String(item.Jump_target ?? ''),
  };
  // 坑位编码按标题语义匹配；编码表外条目（如 Trustpilot）为 undefined，保持原上报。
  const serviceSpmCnt = getServicePromiseSpmPosition(item.title);
  // 坑位 D 位编码（服务保障坑位 service_entry_NN）：曝光与点击共用；模块码或坑位名缺失时不输出。
  const serviceEntrySpmCnt =
    spmModule && serviceSpmCnt ? `${spmModule}.${serviceSpmCnt}` : undefined;
  // Data SPM DOM 打标（请求头 data-spm=1 开关）：与上报 spm_cnt 同值，表外静默不打标。
  const serviceEntryDomSpm = useSpmDomAttr(serviceEntrySpmCnt);
  const { ref: validExposureRef } = useValidExposure({
    // 坑位有效曝光为 item 级，spm_cnt 追加坑位 D 位（服务保障坑位 service_entry_NN）。
    callback: () =>
      onValidExpTrack?.(
        serviceEntrySpmCnt ? withSpmCnt(trackingParams, serviceEntrySpmCnt) : trackingParams,
      ),
  });
  const jumpTarget: JumpTarget = {
    jump_type: item.jump_type,
    Jump_target: item.Jump_target ?? undefined,
    product_group: item.product_group,
    sku: item.sku?.shop_sku ? { shop_sku: item.sku.shop_sku } : undefined,
  };
  const imageAlt = item.title
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  const handleClick = () => {
    if (!onTrack) return;

    try {
      // 坑位点击上报完整 spm_cnt；浅拷贝避免污染曝光共用对象。
      onTrack(serviceEntrySpmCnt ? withSpmCnt(trackingParams, serviceEntrySpmCnt) : trackingParams);
    } catch (error) {
      logger.error('2026 PC OneRowFourNav click tracking error:', error);
    }
  };

  return (
    <li ref={validExposureRef} className="flex min-w-0 items-center justify-center">
      <MagicJump
        prefetch
        item={jumpTarget}
        onBeforeJump={handleClick}
        aria-label={imageAlt}
        className="flex min-w-0 items-center justify-center gap-[6px]"
        {...serviceEntryDomSpm}
      >
        <span className="flex size-[24px] shrink-0 items-center justify-center" aria-hidden>
          <Image
            src={item.imageSrc}
            alt={imageAlt}
            width={24}
            height={24}
            loading={moduleIndex < 3 ? 'eager' : 'lazy'}
            className="block size-full object-contain"
          />
        </span>
        <span
          className="whitespace-nowrap font-br text-14 text-primary-black"
          style={item.text_color ? { color: item.text_color } : undefined}
        >
          {item.title}
        </span>
      </MagicJump>
    </li>
  );
}

export default function OneRowFourNav({
  indexData,
  moduleIndex,
  onTrack,
  onValidExpTrack,
}: OneRowFourNavProps) {
  // 新装修协议使用 content；同时兼容 Block Init 归一化后的扁平结构。
  const content = indexData.content ? { ...indexData, ...indexData.content } : indexData;
  const navigationItems = (content.jump_target || content.items || [])
    .map((item) => ({ ...item, imageSrc: item.jump_image || item.image || '' }))
    .filter((item) => item.imageSrc && item.title)
    .slice(0, MAX_NAV_ITEMS);

  // 楼层 SPM 模块编码（C 位）：按楼块数据动态拼接，坑位码在此基础上追加。
  const spmModule = useHomeSpmModuleCode(indexData.component_id, indexData.id, 'home_pc');
  // Data SPM DOM 打标（C 位模块码，请求头 data-spm=1 开关）：与楼层曝光上报的模块编码同值。
  const moduleDomSpm = useSpmDomAttr(spmModule);

  if (navigationItems.length === 0) return null;

  const moduleId = String(content.blocks_advances_id ?? content.id ?? '');
  const moduleTitle = content.layout_title || '';

  return (
    <div
      data-component-id={indexData.component_id}
      className="w-full px-[24px] py-[8px]"
      style={{ backgroundColor: content.bg_color || '#f2f3f5' }}
      {...moduleDomSpm}
    >
      <ul
        className="mx-auto grid h-[48px] w-full max-w-[1440px]"
        style={{ gridTemplateColumns: `repeat(${navigationItems.length}, minmax(0, 1fr))` }}
      >
        {navigationItems.map((item, index) => (
          <OneRowFourNavItemView
            key={item.id || `${item.title}-${index}`}
            item={item}
            index={index}
            componentId={indexData.component_id}
            moduleId={moduleId}
            moduleIndex={moduleIndex}
            moduleTitle={moduleTitle}
            spmModule={spmModule}
            onTrack={onTrack}
            onValidExpTrack={onValidExpTrack}
          />
        ))}
      </ul>
    </div>
  );
}
